/* Landingpage „Ayurveda Fasten" — das einzige eigene JavaScript der Seite.
 *
 * Vier kleine Aufgaben, keine Bibliothek, kein Framework:
 *   1. Der Scroll-Hinweis über der Vergleichstabelle verschwindet, sobald
 *      gescrollt wurde — und erscheint gar nicht erst, wenn die Tabelle ohnehin
 *      vollständig sichtbar ist.
 *   2. Die klebende Leiste blendet sich zwischen Vergleich und Abschluss ein.
 *   3. Die Vergleichstabelle zeigt zunächst nur die Kernzeilen; der Rest kommt
 *      auf Klick.
 *   4. `aria-expanded` an den FAQ-Zusammenfassungen wird beim Auf- und Zuklappen
 *      nachgeführt. Ein fest ins Markup geschriebener Wert wäre nach dem ersten
 *      Klick falsch — schlimmer als keiner.
 *   5. Die Bewertungszeile der Produktkarten holt Note und Anzahl aus dem
 *      Trusted-Shops-Feed des jeweiligen Produkts.
 *
 * Alles läuft ohne diese Datei weiter: Tabelle bleibt scrollbar UND vollständig,
 * FAQ bleibt bedienbar, nur die Leiste fehlt. Bewusst so.
 */
(() => {
  'use strict';

  /* 1. Scroll-Hinweis der Vergleichstabelle */
  if (!customElements.get('fasten-vergleich')) {
    customElements.define(
      'fasten-vergleich',
      class FastenVergleich extends HTMLElement {
        connectedCallback() {
          this.hinweis = this.querySelector('[data-fasten-hint]');
          this.scroller = this.querySelector('[data-fasten-scroll]');
          if (!this.hinweis || !this.scroller) return;

          this.pruefeUeberlauf();
          window.addEventListener('resize', this.pruefeUeberlauf.bind(this), { passive: true });
          this.scroller.addEventListener('scroll', () => this.verstecke(), { once: true, passive: true });
        }

        // Ohne Überlauf gibt es nichts zu wischen — dann wäre der Hinweis eine
        // Aufforderung ins Leere.
        pruefeUeberlauf() {
          const ueberlauf = this.scroller.scrollWidth > this.scroller.clientWidth + 4;
          this.hinweis.hidden = !ueberlauf;
        }

        verstecke() {
          this.hinweis.hidden = true;
        }
      }
    );
  }

  /* 2. Klebende Leiste zwischen zwei Ankern
   *
   * Bewusst KEIN IntersectionObserver, obwohl er hier naheliegt: Der Observer
   * meldet nur ZUSTANDSWECHSEL. Springt man per Ankerlink („Programme
   * vergleichen") über die Vergleichstabelle hinweg, war sie vorher nicht
   * sichtbar und ist es nachher auch nicht — es kommt kein Ereignis, und die
   * Leiste bliebe aus. Genau dieser Sprung ist auf dieser Seite der Regelfall.
   *
   * Ein auf den Bildaufbau gedrosselter Scroll-Listener kennt dagegen jederzeit
   * die tatsächliche Lage und kostet praktisch nichts.
   */
  if (!customElements.get('fasten-sticky-cta')) {
    customElements.define(
      'fasten-sticky-cta',
      class FastenStickyCta extends HTMLElement {
        connectedCallback() {
          this.start = document.getElementById(this.dataset.start || '');
          this.stop = document.getElementById(this.dataset.stop || '');
          if (!this.start) return;

          this.geplant = false;
          this.pruefen = this.pruefen.bind(this);
          this.anstossen = this.anstossen.bind(this);

          window.addEventListener('scroll', this.anstossen, { passive: true });
          window.addEventListener('resize', this.anstossen, { passive: true });
          this.pruefen();
        }

        anstossen() {
          if (this.geplant) return;
          this.geplant = true;
          requestAnimationFrame(() => {
            this.geplant = false;
            this.pruefen();
          });
        }

        pruefen() {
          const hoehe = window.innerHeight;
          // „Erreicht" heißt: die Oberkante ist im Bild oder schon darüber.
          const startErreicht = this.start.getBoundingClientRect().top <= hoehe * 0.85;
          const stopErreicht = this.stop ? this.stop.getBoundingClientRect().top <= hoehe * 0.9 : false;
          this.setze(startErreicht && !stopErreicht);
        }

        setze(zeigen) {
          if (zeigen === this.zustand) return;
          this.zustand = zeigen;
          if (zeigen) {
            this.hidden = false;
            // Erst im nächsten Bild einblenden, sonst greift der Übergang nicht.
            requestAnimationFrame(() => this.classList.add('is-visible'));
          } else {
            this.classList.remove('is-visible');
          }
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this.anstossen);
          window.removeEventListener('resize', this.anstossen);
        }
      }
    );
  }

  /* 3. Vergleichstabelle: erst die Kernzeilen, Rest auf Klick
   *
   * Wichtig ist die Richtung: Die Seite wird VOLLSTÄNDIG ausgeliefert und erst
   * im Browser gekürzt. Wer ohne JavaScript liest — und jeder, der den
   * Quelltext auswertet — sieht die ganze Tabelle. Umgekehrt wäre es ein
   * Informationsverlust genau an der Stelle, die diese Seite auswertbar macht.
   */
  document.querySelectorAll('[data-fasten-mehr]').forEach((knopf) => {
    const tabelle = document.getElementById(knopf.getAttribute('aria-controls'));
    if (!tabelle || !tabelle.querySelector('.fasten-vergleich__row--detail')) return;

    const auf = knopf.querySelector('[data-mehr-auf]');
    const zu = knopf.querySelector('[data-mehr-zu]');

    tabelle.classList.add('fasten-vergleich--gekuerzt');
    knopf.hidden = false;

    knopf.addEventListener('click', () => {
      const gekuerzt = tabelle.classList.toggle('fasten-vergleich--gekuerzt');
      knopf.setAttribute('aria-expanded', gekuerzt ? 'false' : 'true');
      if (auf) auf.hidden = !gekuerzt;
      if (zu) zu.hidden = gekuerzt;
    });
  });

  /* 4. aria-expanded an den FAQ-Einträgen nachführen */
  const faqBereiche = document.querySelectorAll('.fasten-faq .accordion-details');
  faqBereiche.forEach((details) => {
    const summary = details.querySelector('summary');
    if (!summary) return;
    const setze = () => summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    setze();
    details.addEventListener('toggle', setze);
  });
  /* 5. Bewertungszeile je Produkt (Trusted Shops)
   *
   * Quelle ist derselbe öffentliche Feed, aus dem das Trusted-Shops-Sternewidget
   * im Shop liest: …/feeds/grades/v1/channels/<kanal>/products/sku/<HEX>/feed.json.
   * Die SKU steht dort HEX-kodiert (UTF-8-Bytes als Hex) — im Klartext kommt `{}`.
   * Gezeigt wird der Gesamtwert (`overall`, auf eine Nachkommastelle) mit der
   * Anzahl. Nichts steht im Markup vor: Ohne Antwort oder ohne Bewertung bleibt
   * das Element leer und nimmt keinen Platz ein.
   */
  if (!customElements.get('fasten-bewertung')) {
    const hex = (text) =>
      Array.from(new TextEncoder().encode(text), (b) => b.toString(16).padStart(2, '0')).join('');
    const zahl = (wert) => wert.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    const stern =
      '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false"><path d="M10 1.5l2.6 5.5 6 .7-4.4 4.1 1.2 5.9L10 14.8l-5.4 2.9 1.2-5.9L1.4 7.7l6-.7z" fill="currentColor"/></svg>';

    customElements.define(
      'fasten-bewertung',
      class FastenBewertung extends HTMLElement {
        async connectedCallback() {
          if (this.dataset.geladen) return;
          this.dataset.geladen = '1';
          const { sku, kanal, ziel, name } = this.dataset;
          if (!sku || !kanal) return;
          try {
            const antwort = await fetch(
              `https://integrations.etrusted.com/feeds/grades/v1/channels/${kanal}/products/sku/${hex(sku)}/feed.json`
            );
            if (!antwort.ok) return;
            const daten = await antwort.json();
            const gesamt = daten && daten.grades && daten.grades.overall;
            if (!gesamt || !gesamt.count || typeof gesamt.rating !== 'number') return;
            this.zeige(gesamt.rating, gesamt.count, ziel, name);
          } catch (e) {
            /* Feed nicht erreichbar → keine Zeile. */
          }
        }

        zeige(note, anzahl, ziel, name) {
          const wert = zahl(note);
          const woerter = anzahl === 1 ? '1 Bewertung' : `${anzahl} Bewertungen`;
          const link = document.createElement('a');
          link.className = 'fasten-bewertung__link';
          link.href = ziel || '#';
          link.setAttribute('aria-label', `${name}: ${wert} von 5 Sternen, ${woerter} bei Trusted Shops – zu den Bewertungen`);
          const breite = Math.max(0, Math.min(100, (note / 5) * 100));
          link.innerHTML =
            `<span class="fasten-bewertung__sterne" aria-hidden="true">` +
            `<span class="fasten-bewertung__leer">${stern.repeat(5)}</span>` +
            `<span class="fasten-bewertung__voll" style="width:${breite}%">${stern.repeat(5)}</span>` +
            `</span>` +
            `<span class="fasten-bewertung__text" aria-hidden="true">${wert} (${woerter})</span>`;
          this.appendChild(link);
          this.classList.add('is-geladen');
        }
      }
    );
  }
})();
